import {
  getAllowedFileTypesByChannel,
  getMaxUploadSizeByChannel,
} from '@chatwoot/utils';
import { INBOX_TYPES } from 'dashboard/helper/inbox';
import { ALLOWED_FILE_TYPES } from 'shared/constants/messages';

export const DISCORD_MAX_UPLOAD_SIZE_MB = 10;
export const DISCORD_MAX_CONTENT_LENGTH = 2000;
export const BYTES_PER_MB = 1024 * 1024;
const DEFAULT_MAXIMUM_FILE_UPLOAD_SIZE_MB = 40;

export const DISCORD_IMAGE_CONTENT_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/bmp',
];
export const DISCORD_IMAGE_EXTENSIONS = [
  '.jpg',
  '.jpeg',
  '.png',
  '.gif',
  '.webp',
  '.bmp',
];
export const DISCORD_VIDEO_EXTENSIONS = [
  '.mp4',
  '.webm',
  '.mov',
  '.mpeg',
  '.mpg',
  '.avi',
];
export const DISCORD_DOCUMENT_CONTENT_TYPES = [
  'text/plain',
  'text/csv',
  'text/markdown',
  'application/json',
  'application/pdf',
  'application/zip',
  'application/x-zip-compressed',
  'application/x-7z-compressed',
  'application/gzip',
  'application/x-gzip',
  'application/vnd.rar',
  'application/x-rar-compressed',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/rtf',
  'application/octet-stream',
];
export const DISCORD_EXTENSION_ALLOWLIST = [
  '.txt',
  '.csv',
  '.md',
  '.json',
  '.pdf',
  '.zip',
  '.7z',
  '.gz',
  '.rar',
  '.doc',
  '.docx',
  '.xls',
  '.xlsx',
  '.ppt',
  '.pptx',
  '.rtf',
];
export const DISCORD_DENIED_EXTENSIONS = [
  '.aac',
  '.aiff',
  '.ape',
  '.au',
  '.flac',
  '.m4a',
  '.mid',
  '.midi',
  '.mp3',
  '.oga',
  '.ogg',
  '.opus',
  '.wav',
  '.weba',
  '.wma',
  '.svg',
  '.svgz',
  '.exe',
  '.dll',
  '.bat',
  '.cmd',
  '.com',
  '.scr',
  '.ps1',
  '.sh',
  '.zsh',
  '.bash',
  '.js',
  '.mjs',
  '.vbs',
  '.jar',
];

const DISCORD_PICKER_FILE_TYPES = [
  ...DISCORD_IMAGE_CONTENT_TYPES,
  ...DISCORD_IMAGE_EXTENSIONS,
  ...DISCORD_VIDEO_EXTENSIONS,
  ...DISCORD_EXTENSION_ALLOWLIST,
].join(',');

export const getConversationDirectUploadUrl = ({
  accountId,
  conversationId,
  channelType,
  isPrivateNote = false,
} = {}) => {
  const baseUrl = `/api/v1/accounts/${accountId}/conversations/${conversationId}/direct_uploads`;
  if (channelType === INBOX_TYPES.DISCORD && !isPrivateNote) {
    return `${baseUrl}?purpose=discord_outgoing`;
  }

  return baseUrl;
};

export const getUploadFingerprint = (file, context = {}) => {
  const uploadFile = file?.file || file || {};
  return JSON.stringify([
    context.conversationId || '',
    context.isPrivate ?? '',
    context.selectedContactId || '',
    context.inboxId || '',
    context.channelType || '',
    uploadFile.name || file?.name || '',
    uploadFile.type || file?.type || '',
    uploadFile.size || file?.size || 0,
    uploadFile.lastModified || file?.lastModified || '',
  ]);
};

const normalizeChannelType = ({
  channelType,
  conversationType,
  isInstagramChannel,
}) => {
  if (isInstagramChannel || conversationType === 'instagram_direct_message') {
    return INBOX_TYPES.INSTAGRAM;
  }

  return channelType;
};

const isDiscordOutbound = ({ channelType, isPrivateNote }) =>
  channelType === INBOX_TYPES.DISCORD && !isPrivateNote;

const fileNameFor = file => file?.name || file?.file?.name || '';

const mimeFor = file =>
  (file?.type || file?.file?.type || '').toLowerCase().trim();

const extensionFor = file => {
  const name = fileNameFor(file).toLowerCase();
  const dotIndex = name.lastIndexOf('.');
  return dotIndex >= 0 ? name.slice(dotIndex) : '';
};

const splitAllowedFileTypes = allowedFileTypes =>
  allowedFileTypes
    .split(',')
    .map(type => type.trim())
    .filter(Boolean);

const matchesAllowedType = (file, allowedType) => {
  const extension = extensionFor(file);
  const mime = mimeFor(file);

  if (allowedType === extension) return true;

  if (allowedType.endsWith('/*')) {
    const prefix = allowedType.slice(0, -2);
    return mime.startsWith(`${prefix}/`);
  }

  return allowedType === mime;
};

const isAllowedByList = (file, allowedFileTypes) =>
  splitAllowedFileTypes(allowedFileTypes).some(allowedType =>
    matchesAllowedType(file, allowedType)
  );

const isAllowedDiscordFile = file => {
  const extension = extensionFor(file);
  const mime = mimeFor(file);

  if (!file || file.size === 0) return false;
  if (DISCORD_DENIED_EXTENSIONS.includes(extension)) return false;
  if (mime.startsWith('audio/')) return false;
  if (mime === 'image/svg+xml') return false;

  if (
    DISCORD_IMAGE_CONTENT_TYPES.includes(mime) &&
    DISCORD_IMAGE_EXTENSIONS.includes(extension)
  ) {
    return true;
  }

  if (
    mime.startsWith('video/') &&
    DISCORD_VIDEO_EXTENSIONS.includes(extension)
  ) {
    return true;
  }

  if (
    DISCORD_DOCUMENT_CONTENT_TYPES.includes(mime) &&
    DISCORD_EXTENSION_ALLOWLIST.includes(extension)
  ) {
    return true;
  }

  return mime === '' && DISCORD_EXTENSION_ALLOWLIST.includes(extension);
};

export const getAllowedFileTypesForInbox = ({
  channelType,
  medium,
  isPrivateNote = false,
  isInstagramChannel = false,
  conversationType = '',
} = {}) => {
  const normalizedChannelType = normalizeChannelType({
    channelType,
    conversationType,
    isInstagramChannel,
  });

  if (isPrivateNote) return ALLOWED_FILE_TYPES;
  if (
    isDiscordOutbound({ channelType: normalizedChannelType, isPrivateNote })
  ) {
    return DISCORD_PICKER_FILE_TYPES;
  }

  return getAllowedFileTypesByChannel({
    channelType: normalizedChannelType,
    medium,
  });
};

export const isFileAllowedForInbox = (file, options = {}) => {
  const normalizedChannelType = normalizeChannelType(options);
  const normalizedOptions = {
    ...options,
    channelType: normalizedChannelType,
  };

  if (!file || file.size === 0) return false;
  if (isDiscordOutbound(normalizedOptions)) return isAllowedDiscordFile(file);

  return isAllowedByList(file, getAllowedFileTypesForInbox(normalizedOptions));
};

export const getMaxUploadSizeForInbox = ({
  channelType,
  medium,
  mime,
  installationLimit = DEFAULT_MAXIMUM_FILE_UPLOAD_SIZE_MB,
  isPrivateNote = false,
} = {}) => {
  if (isPrivateNote || !channelType || channelType === INBOX_TYPES.WEB) {
    return installationLimit;
  }

  if (channelType === INBOX_TYPES.DISCORD) {
    return Math.min(DISCORD_MAX_UPLOAD_SIZE_MB, installationLimit);
  }

  const channelLimit = getMaxUploadSizeByChannel({
    channelType,
    medium,
    mime,
  });

  if (channelLimit === DEFAULT_MAXIMUM_FILE_UPLOAD_SIZE_MB) {
    return installationLimit;
  }

  return Math.min(channelLimit, installationLimit);
};

export const getMaxUploadSizeBytesForInbox = options =>
  getMaxUploadSizeForInbox(options) * BYTES_PER_MB;
